import { useEffect, useMemo, useState } from 'react';
import styles from './ScenarioGenerator.module.css';
import { BgmHistorySelector } from './BgmHistorySelector';
import { NumberStepper } from './DurationSelect';
import { SegmentedTabs } from '../../common/SegmentedTabs';
import { SingleSelectDropdown } from '../../common/SingleSelectDropdown';
import { ProviderSegmentedControl } from './ProviderSegmentedControl';
import { BgmSongDetailFields } from './BgmSongDetailFields';
import { useScenarioAttributesStateSafe } from '../state/scenarioAttributesContext';
import { Tooltip } from '../../common/Tooltip';
import { useToast } from '../../../hooks/useToast';
import { playExclusive } from '../../../utils/exclusiveAudio';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import {
  fetchMaterialById,
  fetchMaterialSignedUrl,
} from '../../../features/materials/api';
import { BGM_UPLOAD_ACCEPT, uploadBgmFile } from './bgmUpload';
import { isMotionEngineScenario } from './motionScenario';
import { MotionReplanLink } from './MotionScenarioPanel';
import {
  generateScenarioBgm,
  fetchScenarioBgmPrompt,
  fetchScenarioLyricsPreview,
  syncScenarioLyricsTiming,
  roundScenarioMotionBoundaries,
  type BgmPrompt,
  type MotionBoundaryRoundingResult,
  type LyricsPreviewResponse,
  type LyricsSyncResult,
} from '../../../features/scenarios/api';
import { useUpdateScenario } from '../../../features/scenarios/hooks';
import { copyAudioCatalogItemToMaterials, type AudioCatalogItem } from '../../../features/audioCatalog/api';
import { BgmFreeTrackPicker } from './BgmFreeTrackPicker';
import { BgmUploadDropZone } from './BgmUploadDropZone';
import type { CreditRatesResponse } from '../../../features/organizations/types';
import type { Scenario, ScenarioResponse } from './types';
import { type UseBgmEditorReturn, resolveMaterialIsVocalLead, isLyricsModeScenario } from './hooks/useBgmEditor';
import { useSceneHandlers } from './hooks/SceneHandlersContext';
import { resetAutoVideoCardSegmentation } from './hooks/useAutoVideoCardSegmentation';
import { GENERATED_BGM_LABEL, resolveBgmDisplayName, type BgmDisplayNameSource } from './bgmDisplayName';
import { toBgmDefaultLengthSeconds } from './bgmDefaultLength';
import { deriveProductionMode, isMotionScene } from '../../../features/scenarios/productionMode';
import {
  MOTION_BOUNDARY_ROUNDING_NOTE,
  boundaryRoundingDurations,
  boundaryRoundingInputSignature,
  changedBoundaryRoundingRows,
  describeBoundaryRoundingSkip,
} from './motionBoundaryRounding';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const EMPTY_BGM_PROMPT: BgmPrompt = {
  song_description: '',
  is_instrumental: true,
  inspiration_tags: [],
  song_title: '',
};

// 歌詞モード (歌モノ) だけの既定値。通常モードは空から始め、未入力ならサーバ側の
// 既定テンプレート / 自動生成 (おまかせ) に任せる。
const DEFAULT_LYRICS_BGM_PROMPT: BgmPrompt = {
  ...EMPTY_BGM_PROMPT,
  genre: 'J-POP ダンスグループ',
  bpm: 130,
};

const LYRICS_GENRES = ['J-POP ダンスグループ', '高速ラップ', 'バラード', '童謡'];
const LYRICS_GENRE_OPTIONS = [
  ...LYRICS_GENRES.map((genre) => ({ value: genre, label: genre })),
  { value: 'custom', label: 'カスタム' },
];

/**
 * Gemini (Lyria) の既定 BGM プロンプト (song_description)。
 * バックエンド bgm_generator.gemini_default_song_description と文面を揃える。
 * 参照画像トグルの切替時、未編集テンプレートの判定・引き直しに使う。
 * 動画生成モードにより添付される画像が変わる (storyboard: ストーリーボード /
 * frame: 各シーンのフレーム画像 / reference: 参考画像)。
 */
function geminiDefaultSongDescription(
  summary: string | undefined,
  hasReferenceImages: boolean,
  videoGenerationMode: string | null,
): string {
  if (hasReferenceImages) {
    if (videoGenerationMode === 'frame') {
      return '添付の各シーンのフレーム画像のトーンやムードからインスパイアされた曲。';
    }
    if (videoGenerationMode === 'reference') {
      return '添付の参考画像のトーンやムードからインスパイアされた曲。';
    }
    return '添付のCMストーリーボードのストーリーのトーンやムードからインスパイアされた曲。';
  }
  const s = (summary || '').trim();
  return s
    ? `以下のストーリーからインスパイアされた曲。\nストーリー概要: ${s}`
    : '以下のストーリーからインスパイアされた曲。';
}

/** 歌手選択 (性別) + 声質の自由記述から、Lyria に渡す英語1文を組み立てる (§5.5)。 */
export type SingerGender = 'unspecified' | 'female' | 'male';

export function buildSingerProfileText(gender: SingerGender, freeText: string): string | null {
  const text = freeText.trim();
  if (gender === 'unspecified' && !text) return null;
  const genderLabel = gender === 'female' ? 'Female vocal' : gender === 'male' ? 'Male vocal' : 'Vocal';
  if (!text) return `${genderLabel}.`;
  // 自由記述の末尾に句点が無ければ付ける (Lyria プロンプトの他の文と体裁を揃える)。
  const normalized = /[.!?]$/.test(text) ? text : `${text}.`;
  return `${genderLabel}, ${normalized}`;
}

/** BGM 素材の generation_metadata から歌詞比較表示用のフィールドだけを取り出す。 */
export type GeneratedLyricsMeta = {
  lyrics_input?: string;
  lyrics_sections?: { tag: string | null; kind: string | null; index: number | null; lines: string[] }[];
};

export function extractGeneratedLyricsMeta(
  material: { generation_metadata?: Record<string, unknown> | null } | null | undefined,
): GeneratedLyricsMeta | null {
  const meta = material?.generation_metadata;
  if (!meta || meta.is_vocal_lead !== true) return null;
  const lyricsInput = typeof meta.lyrics_input === 'string' ? meta.lyrics_input : undefined;
  const lyricsSections = Array.isArray(meta.lyrics_sections)
    ? (meta.lyrics_sections as GeneratedLyricsMeta['lyrics_sections'])
    : undefined;
  if (!lyricsInput && !lyricsSections) return null;
  return { lyrics_input: lyricsInput, lyrics_sections: lyricsSections };
}

interface BgmSectionProps {
  scenario: Scenario;
  bgm: UseBgmEditorReturn;
  lastResult: ScenarioResponse | null;
  setLastResult: React.Dispatch<React.SetStateAction<ScenarioResponse | null>>;
  creditRatesData: CreditRatesResponse | undefined;
  updateScenarioMutation: ReturnType<typeof useUpdateScenario>;
  getSceneVideoSettings: (sceneKey: string) => {
    duration: number;
    generateAudio: boolean;
    videoModel?: string;
    resolution?: string;
  };
  handleDownloadMaterial: (materialId: string, namePrefix: string) => void;
}

export function BgmSection({
  scenario,
  bgm,
  lastResult,
  setLastResult,
  creditRatesData,
  updateScenarioMutation,
  getSceneVideoSettings,
  handleDownloadMaterial,
}: BgmSectionProps) {
  const toast = useToast();

  const {
    bgmGeneratingScenarios,
    startBgmGeneration,
    finishBgmGeneration,
    isBgmScenarioActive,
    bgmUploading,
    setBgmUploading,
    bgmAudioUrl,
    setBgmAudioUrl,
    bgmFileName,
    setBgmFileName,
    bgmFileInputRef,
    bgmExpanded,
    setBgmExpanded,
    bgmPlaying,
    setBgmPlaying,
    bgmUnplayed,
    setBgmUnplayed,
    bgmAudioRef,
    bgmSongTitle,
    setBgmSongTitle,
    bgmDuration,
    setBgmDuration,
    bgmLengthSeconds,
    setBgmLengthSeconds,
    bgmPromptDraft,
    setBgmPromptDraft,
    bgmPromptEditing,
    setBgmPromptEditing,
    bgmPromptFetching,
    setBgmPromptFetching,
    bgmProvider,
    setBgmProvider,
    bgmUseStoryboardRef,
    setBgmUseStoryboardRef,
    bgmVocal,
    setBgmVocal,
    bgmUploadMode,
    setBgmUploadMode,
    bgmFreeTrackMode,
    setBgmFreeTrackMode,
    bgmCatalogItemId,
    setBgmCatalogItemId,
    bgmPromptMode,
    selectBgmPromptMode,
    bgmTemplateKey,
    setBgmTemplateKey,
    bgmLyricsLocked,
    setBgmIsVocalLead,
  } = bgm;

  const bgmGenerating = !!bgmGeneratingScenarios[scenario.id];
  // アップロード / フリー楽曲タブは生成しない (尺ステッパー・生成ボタン・生成設定を出さない)。
  const bgmManualMode = bgmUploadMode || bgmFreeTrackMode;
  const isValidScenarioId = UUID_REGEX.test(scenario.id);
  // フリー楽曲の複製・紐付け中のカタログ項目 id (一覧の該当行を「設定中…」にする)
  const [freeTrackBusyId, setFreeTrackBusyId] = useState<string | null>(null);
  const isLyricsPromptMode = bgmPromptMode === 'lyrics';
  const baseBgmPrompt = isLyricsPromptMode ? DEFAULT_LYRICS_BGM_PROMPT : EMPTY_BGM_PROMPT;
  const effectivePromptDraft = bgmPromptDraft ?? baseBgmPrompt;
  // プリセットと同じ文字列でも「カスタム」を選んだ直後は入力欄を表示する。
  const [customGenreSelected, setCustomGenreSelected] = useState(false);
  const isCustomGenre = customGenreSelected || !LYRICS_GENRES.includes(effectivePromptDraft.genre ?? '');
  useEffect(() => { setCustomGenreSelected(false); }, [scenario.id]);

  // 歌手プロファイル (任意・§5.5)。localStorage には保持しない (シナリオ切替でリセット)。
  const [bgmSingerGender, setBgmSingerGender] = useState<SingerGender>('unspecified');
  const [bgmSingerText, setBgmSingerText] = useState('');
  useEffect(() => {
    setBgmSingerGender('unspecified');
    setBgmSingerText('');
  }, [scenario.id]);

  // 歌詞プレビュー (GET lyrics-preview・課金なし)。歌詞モードに入ったとき / シナリオが
  // アクティブになったとき / シーンの narration が変わったときに取得し直す。
  const [lyricsPreview, setLyricsPreview] = useState<LyricsPreviewResponse | null>(null);
  const [lyricsPreviewLoading, setLyricsPreviewLoading] = useState(false);
  const narrationSignature = (scenario.scenes || [])
    .map((s) => `${s.order}:${s.narration ?? ''}`)
    .join('|');
  useEffect(() => {
    if (!isLyricsPromptMode || !isValidScenarioId) {
      setLyricsPreview(null);
      return;
    }
    let cancelled = false;
    setLyricsPreviewLoading(true);
    fetchScenarioLyricsPreview(scenario.id)
      .then((res) => {
        if (!cancelled) setLyricsPreview(res);
      })
      .catch(() => {
        if (!cancelled) setLyricsPreview({ ok: false, reason: '歌詞プレビューの取得に失敗しました' });
      })
      .finally(() => {
        if (!cancelled) setLyricsPreviewLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLyricsPromptMode, isValidScenarioId, scenario.id, narrationSignature]);

  // 生成後の歌詞比較 (「送った歌詞」/「Lyria が返した歌詞」・§5.5)。折りたたみ既定 off。
  const [generatedLyricsMeta, setGeneratedLyricsMeta] = useState<GeneratedLyricsMeta | null>(null);
  const [lyricsComparisonExpanded, setLyricsComparisonExpanded] = useState(false);
  // 歌 → 映像の再同期 (POST lyrics-sync) の実行中フラグ
  const [lyricsSyncing, setLyricsSyncing] = useState(false);

  // 歌 → 映像 (計画 md §5.5.1): BE が歌の実測でコマ尺を書き戻したら、画面のシーン (lastResult) と
  // コマ尺のローカル state を同じ値にパッチする (再取得しない)。generation_metadata.lyrics_sync は
  // 動画生成ゲート解除・自動分割の判断材料。BGM 生成完了と再同期ボタンの両方から使う。
  const applyLyricsSyncToState = (sync: LyricsSyncResult, materialId: string) => {
    if (sync.status !== 'applied' || sync.scenes.length === 0) return;
    const secondsByScene = new Map(sync.scenes.map((s) => [s.scene_id, s.seconds] as const));
    setLastResult((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        scenarios: prev.scenarios.map((s) => {
          if (s.id !== scenario.id) return s;
          return {
            ...s,
            generation_metadata: {
              ...(s.generation_metadata ?? {}),
              lyrics_sync: {
                bgm_material_id: materialId,
                status: 'applied',
                total_seconds: sync.total_seconds,
                scene_count: sync.scene_count,
                matched_count: sync.matched_count,
              },
            },
            scenes: (s.scenes || []).map((sc) => (
              secondsByScene.has(sc.id)
                ? { ...sc, storyboard_duration_seconds: secondsByScene.get(sc.id) }
                : sc
            )),
          };
        }),
      };
    });
    const entries: Record<string, number> = {};
    for (const sc of scenario.scenes || []) {
      const seconds = secondsByScene.get(sc.id);
      if (seconds != null) entries[`${scenario.id}-scene-${sc.order}`] = seconds;
    }
    applySceneStoryboardDurations(entries);
    // コマ尺の合計が変わったので動画カードの自動分割をやり直す (歌が想定尺より長いと
    // 1 カードでは動画モデルの最大尺を超えるため)。まっさらなシナリオでのみ効く。
    resetAutoVideoCardSegmentation(scenario.id);
  };

  // フリー楽曲 (運営 / パートナー提供カタログ) を BGM に設定する
  // (docs/features/free-bgm-catalog-plan.md)。カタログは組織の素材ではないので、動画エディタの
  // 効果音ピッカーと同じく `copy-to-materials` で個人フォルダの素材へ複製してから
  // bgm_material_id に紐付ける (同じ曲を選び直しても素材は増えない・冪等)。
  const handlePickFreeTrack = async (item: AudioCatalogItem): Promise<boolean> => {
    if (!isValidScenarioId || freeTrackBusyId) return false;
    setFreeTrackBusyId(item.id);
    try {
      const copied = await copyAudioCatalogItemToMaterials(item.id);
      const materialId = copied.material_id;
      await updateScenarioMutation.mutateAsync({
        scenarioId: scenario.id,
        // 選んだら「採用しない」は解除する (アップロード・履歴切替と同じ)
        payload: { bgm_material_id: materialId, bgm_excluded: false },
      });
      if (lastResult) {
        setLastResult((prev) => {
          if (!prev) return prev;
          const updatedScenarios = prev.scenarios.map((s) =>
            s.id === scenario.id ? { ...s, bgm_material_id: materialId, bgm_excluded: false } : s,
          );
          return { ...prev, scenarios: updatedScenarios };
        });
      }
      const displayName = copied.name || item.name;
      // 表示状態 (再生 URL・曲名・尺・選択中) は useBgmEditor が全シナリオで共有している。
      // 処理中に別シナリオへ移っていたら (保存先は元のシナリオで正しいので) 表示だけ触らない
      // (BGM 生成完了と同じガード・レビュー P2)。切替先の表示は useBgmEditor の切替 effect が引く。
      if (isBgmScenarioActive(scenario.id)) {
        let url: string | null = null;
        try {
          url = await fetchMaterialSignedUrl(materialId, true);
        } catch {
          url = null;
        }
        // 署名 URL の取得中にも切り替わりうるので、もう一度確かめてから反映する
        if (isBgmScenarioActive(scenario.id)) {
          setBgmAudioUrl(url);
          setBgmFileName(displayName);
          setBgmSongTitle(displayName);
          setBgmDuration(copied.duration_seconds ?? item.duration_seconds ?? null);
          // フリー楽曲は常に通常 BGM 扱い (is_vocal_lead を付けない)。
          setBgmIsVocalLead(false);
          setGeneratedLyricsMeta(null);
          setBgmCatalogItemId(item.id);
        }
      }
      toast.success(`「${displayName}」をBGMに設定しました`);
      return true;
    } catch (error) {
      // BE の理由 (容量超過の案内文など) はそのまま見せる
      toast.error(getErrorMessage(error, 'フリー楽曲の設定に失敗しました'));
      return false;
    } finally {
      setFreeTrackBusyId(null);
    }
  };

  // 素材パネルからドラッグした音声素材 (application/json・buildDragData の形) をアップロード領域に落として
  // BGM にする (利用者要望 2026-09-30「素材パネルから BGM をドラッグ&ドロップできない」)。ファイルの再アップロードは
  // せず、既存の素材をそのまま bgm_material_id に紐付ける (履歴の切替と同じ手順・「採用しない」は解除)
  const handleDropExistingBgmMaterial = async (
    asset: { id?: unknown; contentType?: unknown; audioText?: unknown; durationSeconds?: unknown },
  ): Promise<boolean> => {
    const materialId = typeof asset.id === 'string' ? asset.id : null;
    if (!materialId || !isValidScenarioId) return false;
    if (typeof asset.contentType !== 'string' || !asset.contentType.startsWith('audio/')) {
      toast.error('音声素材のみ BGM にできます');
      return false;
    }
    setBgmUploading(true);
    try {
      await updateScenarioMutation.mutateAsync({
        scenarioId: scenario.id,
        payload: { bgm_material_id: materialId, bgm_excluded: false },
      });
      if (lastResult) {
        setLastResult((prev) => {
          if (!prev) return prev;
          const updatedScenarios = prev.scenarios.map((s) =>
            s.id === scenario.id ? { ...s, bgm_material_id: materialId, bgm_excluded: false } : s,
          );
          return { ...prev, scenarios: updatedScenarios };
        });
      }
      let displayName = (typeof asset.audioText === 'string' && asset.audioText) || 'BGM';
      // 表示状態は useBgmEditor が全シナリオで共有している (フリー楽曲と同じガード)
      if (isBgmScenarioActive(scenario.id)) {
        let url: string | null = null;
        try {
          url = await fetchMaterialSignedUrl(materialId, true);
        } catch {
          url = null;
        }
        let mat: Awaited<ReturnType<typeof fetchMaterialById>> | null = null;
        try {
          mat = await fetchMaterialById(materialId);
        } catch {
          mat = null;
        }
        if (isBgmScenarioActive(scenario.id)) {
          setBgmAudioUrl(url);
          displayName = (mat && resolveBgmDisplayName(mat as BgmDisplayNameSource)) || displayName;
          setBgmFileName(displayName);
          const meta = (mat as { generation_metadata?: { song_title?: string } } | null)?.generation_metadata;
          setBgmSongTitle(meta?.song_title || null);
          const dur = (mat as { duration_seconds?: number } | null)?.duration_seconds;
          setBgmDuration(dur || (typeof asset.durationSeconds === 'number' ? asset.durationSeconds : null));
          setBgmIsVocalLead(mat ? resolveMaterialIsVocalLead(mat as { generation_metadata?: Record<string, unknown> | null }) : false);
          setGeneratedLyricsMeta(mat ? extractGeneratedLyricsMeta(mat as { generation_metadata?: Record<string, unknown> | null }) : null);
          // フリー楽曲の複製 (generation_metadata.audio_catalog_item_id) なら選択中の曲として示す (useBgmEditor の切替 effect と同じ)
          const catalogId = (mat as { generation_metadata?: { audio_catalog_item_id?: unknown } } | null)?.generation_metadata?.audio_catalog_item_id;
          setBgmCatalogItemId(typeof catalogId === 'string' && catalogId ? catalogId : null);
        }
      }
      toast.success(`「${displayName}」をBGMに設定しました`);
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error, 'BGMの設定に失敗しました'));
      return false;
    } finally {
      setBgmUploading(false);
    }
  };

  // 解析できても書き戻さなかった理由をユーザー向けに言い換える
  const describeLyricsSyncSkip = (sync: LyricsSyncResult): string => {
    if (sync.reason === 'low_match') {
      return `歌詞の聞き取りが足りず (${sync.matched_count}/${sync.scene_count} シーン)、シーンの尺は変更しませんでした。BGM を再生成するか、歌詞を聞き取りやすい語に直してお試しください`;
    }
    if (sync.reason === 'no_words' || sync.reason.startsWith('whisper_skipped')) {
      return '歌の文字起こしができなかったため、シーンの尺は変更しませんでした';
    }
    return `シーンの尺は変更しませんでした (${sync.reason})`;
  };
  useEffect(() => {
    setGeneratedLyricsMeta(null);
    setLyricsComparisonExpanded(false);
  }, [scenario.id]);

  // ストーリーボードモードの BGM 長さの既定値 = シーンのコマ尺合計 (+ ブランドカット表示尺)。
  // 想定尺はシナリオ編集で実態と乖離していくため、タイムラインの実合計を既定にする
  // (絵コンテの想定尺表示と同方針)。編集中のコマ尺は DB 反映前でも SceneHandlersContext の
  // ローカル state から取れるので、ステッパーの表示・生成 payload ともライブに追従する。
  // 他モードは従来どおり null (未指定 → backend が想定尺 > シーン合計の順で解決) を維持。
  const { videoGenerationMode, getSceneStoryboardDuration, applySceneStoryboardDurations, updateSceneVideoSettings } = useSceneHandlers();

  // BGM の小節頭に合わせてシーンの尺を丸める (計画 md §15.5・Phase 2d)。per_scene で BGM 採用中かつ
  // モーションのシーンがあるときだけ出す。プレビュー (apply=false) → 「適用」で書き戻し。ポイント消費なし。
  const [boundaryRounding, setBoundaryRounding] = useState<MotionBoundaryRoundingResult | null>(null);
  const [boundaryRoundingBusy, setBoundaryRoundingBusy] = useState(false);
  const canRoundMotionBoundaries = isValidScenarioId
    && deriveProductionMode(scenario) === 'per_scene'
    && !!scenario.bgm_material_id && !scenario.bgm_excluded
    && (scenario.scenes || []).some((sc) => isMotionScene(sc));
  // シナリオが切り替わったら、またはプレビューの前提 (BGM・シーンの尺 / 順序 / 動画・ナレーション尺) が変わったら
  // プレビューを閉じる (適用 API は今の DB から再計算するので、見せた内容と違う値を適用させない。
  // backend も fingerprint で突き合わせ、変わっていれば status='stale' で書かない)
  const boundaryRoundingSignature = boundaryRoundingInputSignature(scenario);
  useEffect(() => { setBoundaryRounding(null); }, [scenario.id, boundaryRoundingSignature]);

  // BE が書き戻した予定尺を画面のシーン (lastResult) と動画設定のローカル state にパッチする
  // (歌詞同期の applyLyricsSyncToState と同じ流儀で再取得しない。DB は更新済みなので PUT しない)。
  const applyBoundaryRoundingToState = (result: MotionBoundaryRoundingResult) => {
    const secondsByScene = boundaryRoundingDurations(result);
    if (secondsByScene.size === 0) return;
    setLastResult((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        scenarios: prev.scenarios.map((s) => {
          if (s.id !== scenario.id) return s;
          return {
            ...s,
            scenes: (s.scenes || []).map((sc) => (
              secondsByScene.has(sc.id) ? { ...sc, video_duration_seconds: secondsByScene.get(sc.id) } : sc
            )),
          };
        }),
      };
    });
    for (const sc of scenario.scenes || []) {
      const seconds = secondsByScene.get(sc.id);
      if (seconds != null) updateSceneVideoSettings(`${scenario.id}-scene-${sc.order}`, { duration: seconds }, { skipPersist: true });
    }
  };

  const handlePreviewBoundaryRounding = async () => {
    if (boundaryRoundingBusy) return;
    setBoundaryRoundingBusy(true);
    try {
      const res = await roundScenarioMotionBoundaries(scenario.id, false);
      if (!res.ok || res.changed === 0) {
        setBoundaryRounding(null);
        if (res.ok) toast.show(describeBoundaryRoundingSkip(res));
        else toast.error(describeBoundaryRoundingSkip(res));
        return;
      }
      setBoundaryRounding(res);
    } catch (err) {
      toast.error(getErrorMessage(err, 'シーンの尺の丸めを計算できませんでした'));
    } finally {
      setBoundaryRoundingBusy(false);
    }
  };

  const handleApplyBoundaryRounding = async () => {
    if (boundaryRoundingBusy) return;
    setBoundaryRoundingBusy(true);
    try {
      const res = await roundScenarioMotionBoundaries(scenario.id, true, boundaryRounding?.fingerprint);
      if (res.status === 'applied') {
        applyBoundaryRoundingToState(res);
        toast.success(`BGM の小節に合わせてシーンの尺を更新しました (${res.changed} シーン)`);
      } else if (res.status === 'stale') {
        toast.error(res.reason || 'プレビューの後にシナリオが変わりました。もう一度プレビューしてください');
      } else {
        toast.error(describeBoundaryRoundingSkip(res));
      }
      setBoundaryRounding(null);
    } catch (err) {
      toast.error(getErrorMessage(err, 'シーンの尺の丸めに失敗しました'));
    } finally {
      setBoundaryRoundingBusy(false);
    }
  };
  const defaultBgmLengthSeconds = useMemo<number | null>(() => {
    if (videoGenerationMode !== 'storyboard') return null;
    const scenes = scenario.scenes || [];
    if (scenes.length === 0) return null;
    let total = 0;
    for (const s of scenes) {
      if (s.brand_cut_position) {
        // ブランドカットは紐付け動画の実尺 > 表示秒数 (getSceneDurations と同じ規則)
        const boundVideoDur = s.video_material_id
          ? (s.video_duration_seconds
              || ((s.generation_metadata as Record<string, unknown> | undefined)?.duration_seconds as number | undefined)
              || 0)
          : 0;
        total += boundVideoDur > 0 ? boundVideoDur : (s.brand_cut_duration_seconds ?? 3);
      } else {
        total += getSceneStoryboardDuration(
          `${scenario.id}-scene-${s.order}`,
          s.storyboard_duration_seconds ?? 2,
        );
      }
    }
    // 30秒未満は30秒へ切り上げ (エディタでトリムできるため)、それ以上は合計に合わせる
    return total > 0 ? toBgmDefaultLengthSeconds(total) : null;
  }, [videoGenerationMode, scenario, getSceneStoryboardDuration]);
  // ユーザーが明示入力した値が最優先。未入力ならシーン合計の既定値。
  const effectiveBgmLengthSeconds = bgmLengthSeconds ?? defaultBgmLengthSeconds;

  // 「曲の詳細設定」(ジャンル/ムード/BPM/キー) の開閉。既定は通常モード=閉じる /
  // 歌詞モード=開く (既定のジャンル/BPM を使う)。既定が違うためキーもモード別に分ける。
  // scenario 単位で localStorage に保持する (リロード/シナリオ切替で OFF に戻ると、
  // 入力済みの genre が送信時に空へ落とされ生成が失敗するのを防ぐ)。
  const bgmDetailOpenKey = isLyricsPromptMode
    ? `bgm_lyrics_detail_open_${scenario.id}`
    : `bgm_detail_open_${scenario.id}`;
  const bgmDetailOpenDefault = isLyricsPromptMode;
  const readBgmDetailOpen = () => {
    try {
      const stored = localStorage.getItem(bgmDetailOpenKey);
      return stored == null ? bgmDetailOpenDefault : stored === '1';
    } catch {
      return bgmDetailOpenDefault;
    }
  };
  const [bgmDetailOpen, setBgmDetailOpen] = useState<boolean>(readBgmDetailOpen);
  const handleBgmDetailToggle = (checked: boolean) => {
    setBgmDetailOpen(checked);
    try {
      localStorage.setItem(bgmDetailOpenKey, checked ? '1' : '0');
    } catch {
      /* localStorage 不可環境では永続化しない */
    }
  };
  // useState の初期化関数は初回マウントしか走らない。呼び出し元がアクティブな
  // シナリオだけを描画しており、同一インスタンスが再利用されるケースに備えて、
  // scenario.id / モード変更時に localStorage から bgmDetailOpen を再同期する。
  // eslint-disable-next-line react-hooks/exhaustive-deps -- readBgmDetailOpen はキーだけに依存
  useEffect(() => { setBgmDetailOpen(readBgmDetailOpen()); }, [bgmDetailOpenKey]);

  // BGM 生成プロバイダ。Lyria クリップは固定尺、Lyria Pro は尺・曲展開を指定可能。
  const isLyriaClip = bgmProvider === 'lyria';
  const isLyriaPro = bgmProvider === 'lyria-pro';
  // Lyria 系は参照画像 (最大10枚) を曲に反映できる
  const isLyriaFamily = isLyriaClip || isLyriaPro;
  // 作曲に添付される画像は動画生成モードで変わる (backend _resolve_bgm_reference_parts)。
  // トグル文言もモードに合わせる。
  const bgmReferenceImageLabel =
    videoGenerationMode === 'frame' ? 'フレーム画像'
      : videoGenerationMode === 'reference' ? '参考画像'
        : 'ストーリーボード画像';

  // BGM テンプレート (docs/features/bgm-prompt-template-plan.md)。属性ツリーに field が無ければ
  // 空配列でセレクタ自体を出さず、標準モードに固定する。
  const scenarioAttributesState = useScenarioAttributesStateSafe();
  const bgmTemplateOptions = scenarioAttributesState?.bgmTemplateOptions ?? [];
  // 属性ツリー取得前は「テンプレート無し」と区別できないため、選択中モードがテンプレートの間は
  // 取得完了まで生成を止める (取得前に標準モードで生成してポイントを消費しないように)。
  // Provider 外 (テスト等) は待たない。
  const templateOptionsPending =
    bgmPromptMode === 'template' && scenarioAttributesState != null && !scenarioAttributesState.scenarioAttributesLoaded;
  const templateMode = bgmPromptMode === 'template' && bgmTemplateOptions.length > 0;
  // 選択中 key が一覧に無ければ先頭へ丸める (OEM 非表示 / 削除された option への追従)
  const effectiveTemplateKey =
    bgmTemplateOptions.find((o) => o.value === bgmTemplateKey)?.value
    ?? bgmTemplateOptions[0]?.value
    ?? null;

  const persistPromptDraft = (next: BgmPrompt) => {
    localStorage.setItem(`bgm_prompt_override_${scenario.id}`, JSON.stringify(next));
  };

  const updatePromptDraft = (patch: Partial<BgmPrompt>) => {
    setBgmPromptDraft((prev) => {
      const next: BgmPrompt = { ...(prev ?? baseBgmPrompt), ...patch };
      persistPromptDraft(next);
      return next;
    });
  };

  return (
    <div className={styles.flowStepSection} style={{ marginTop: '24px' }}>
      <input
        ref={bgmFileInputRef}
        type="file"
        accept={BGM_UPLOAD_ACCEPT}
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          e.target.value = '';
          setBgmUploading(true);
          try {
            const { materialId, name: uploadedName } = await uploadBgmFile(file, { scenarioId: scenario.id });
            await updateScenarioMutation.mutateAsync({
              scenarioId: scenario.id,
              // アップロードで差し替えたら「採用しない」は解除する
              payload: { bgm_material_id: materialId, bgm_excluded: false },
            });
            if (lastResult) {
              setLastResult((prev) => {
                if (!prev) return prev;
                const updatedScenarios = prev.scenarios.map((s) =>
                  s.id === scenario.id ? { ...s, bgm_material_id: materialId, bgm_excluded: false } : s,
                );
                return { ...prev, scenarios: updatedScenarios };
              });
            }
            try {
              const url = await fetchMaterialSignedUrl(materialId, true);
              setBgmAudioUrl(url);
            } catch {
              setBgmAudioUrl(null);
            }
            setBgmFileName(uploadedName);
            setBgmSongTitle(uploadedName);
            // アップロードした BGM は常に通常 BGM 扱い (is_vocal_lead を付けない・§3.6 決定8)。
            setBgmIsVocalLead(false);
            setGeneratedLyricsMeta(null);
            // アップロード後に素材情報から秒数を取得
            fetchMaterialById(materialId)
              .then((mat) => {
                const dur = (mat as { duration_seconds?: number } | null)?.duration_seconds;
                if (dur) setBgmDuration(dur);
              })
              .catch(() => {});
            toast.success('BGMをアップロードしました');
          } catch {
            toast.error('BGMのアップロードに失敗しました');
          } finally {
            setBgmUploading(false);
          }
        }}
      />
      <div
        className={styles.stepHeader}
        onClick={() => {
          setBgmExpanded((prev) => !prev);
          if (!bgmExpanded && scenario.bgm_material_id && !bgmAudioUrl) {
            fetchMaterialSignedUrl(scenario.bgm_material_id, true)
              .catch(() => fetchMaterialSignedUrl(scenario.bgm_material_id!, false))
              .then((url) => setBgmAudioUrl(url))
              .catch(() => setBgmAudioUrl(null));
            if (!bgmFileName) {
              fetchMaterialById(scenario.bgm_material_id)
                .then((m) => {
                  if (m) {
                    const displayName = resolveBgmDisplayName(m as BgmDisplayNameSource);
                    if (displayName) setBgmFileName(displayName);
                    const meta = (m as { generation_metadata?: { song_title?: string } }).generation_metadata;
                    if (meta?.song_title) setBgmSongTitle(meta.song_title);
                    setGeneratedLyricsMeta(extractGeneratedLyricsMeta(m as { generation_metadata?: Record<string, unknown> | null }));
                  }
                })
                .catch(() => {});
            }
          }
        }}
        style={{ cursor: 'pointer' }}
      >
        <div className={styles.stepTitle}>
          <span
            className={styles.expandTriangle}
            style={{ transform: bgmExpanded ? 'rotate(90deg)' : 'none' }}
          >
            <svg width="12" height="12" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 1l4 3-4 3" /></svg>
          </span>
          {/* 折りたたみ時のみ、生成済み BGM があればシェブロンの右に再生/停止ボタン。 */}
          {!bgmExpanded && scenario.bgm_material_id && (
            <button
              type="button"
              className={`${styles.collapsedPlayBtn} ${bgmUnplayed && !bgmPlaying ? styles.bgmPlayUnplayed : ''}`}
              title={bgmPlaying ? '停止' : 'BGMを再生'}
              onClick={async (e) => {
                e.stopPropagation();
                if (bgmPlaying) {
                  bgmAudioRef.current?.pause();
                  return;
                }
                if (bgmAudioRef.current && bgmAudioUrl) {
                  bgmAudioRef.current.play();
                  return;
                }
                // URL 未ロード時は取得してから、audio 要素のマウントを待って再生。
                const url = await fetchMaterialSignedUrl(scenario.bgm_material_id!, true)
                  .catch(() => fetchMaterialSignedUrl(scenario.bgm_material_id!, false))
                  .catch(() => null);
                if (url) {
                  setBgmAudioUrl(url);
                  requestAnimationFrame(() => requestAnimationFrame(() => bgmAudioRef.current?.play()));
                }
              }}
            >
              <img
                src={bgmPlaying ? '/button_stop_orange.svg' : '/button_play_orange.svg'}
                alt={bgmPlaying ? '停止' : '再生'}
                style={{ width: '24px', height: '24px' }}
              />
            </button>
          )}
          <span style={{ whiteSpace: 'nowrap' }}>BGM</span>
          {!bgmExpanded && scenario.bgm_material_id && (bgmFileName || bgmSongTitle) && (
            <span style={{ fontWeight: 400, color: 'rgba(245, 241, 230, 0.4)', marginLeft: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              {bgmFileName || bgmSongTitle}
            </span>
          )}
        </div>
        <div className={styles.bgmHeaderRight}>
          {/* アップロード / フリー楽曲タブのときは生成しないため、尺ステッパー・生成ボタンは
              非活性化する (高さを保ってレイアウトのズレを防ぐ)。 */}
          <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
            {bgmManualMode ? (
              // アップロード / フリー楽曲タブは生成しないため、尺は「-秒」で非活性表示にする。
              <NumberStepper
                value={null}
                onChange={() => {}}
                placeholder="-秒"
                readOnly
                disabled
              />
            ) : isLyricsPromptMode ? (
              // 歌詞モードは尺ステッパー非表示。想定尺 (lyrics-preview の結果) をそのまま表示する
              // (§5.5)。取得前 / 前提エラー時はシーン尺の合計フォールバック表示に留める。
              <span style={{ fontSize: '13px', color: 'rgba(245, 241, 230, 0.5)', whiteSpace: 'nowrap' }}>
                {lyricsPreview?.ok
                  ? `想定尺 ${Math.round(lyricsPreview.target_total_sec)}秒で作曲します`
                  : (effectiveBgmLengthSeconds
                      ? `シーン尺の合計 ${effectiveBgmLengthSeconds}秒で作曲します`
                      : '想定尺で作曲します')}
              </span>
            ) : isLyriaClip ? (
              // Lyria クリップは30秒の固定尺。ElevenLabs と同じステッパー見た目で固定表示する。
              <Tooltip content="30秒固定">
                <NumberStepper
                  value={30}
                  onChange={() => {}}
                  suffix="秒"
                  readOnly
                  disabled={bgmGenerating}
                />
              </Tooltip>
            ) : (
              <NumberStepper
                value={effectiveBgmLengthSeconds}
                onChange={(v) => setBgmLengthSeconds(v)}
                min={isLyriaPro ? 10 : 3}
                max={600}
                step={1}
                suffix="秒"
                placeholder="秒"
                disabled={bgmGenerating}
              />
            )}
          </div>
          <button
            type="button"
            className={`${styles.bgmGenerateButton} ${bgmGenerating ? 'ai-loading-active' : ''}`}
            onClick={async (e) => {
              e.stopPropagation();
              if (bgmGenerating || bgmUploadMode || templateOptionsPending) return;
              if (isLyricsPromptMode && (!lyricsPreview || !lyricsPreview.ok)) return;
              if (!startBgmGeneration(scenario.id)) return;
              try {
                // 「曲の詳細を設定」が OFF のときは genre/mood/bpm/key を送らない
                // (下書きに残っていてもパラメータとして反映しない)。
                const effectiveOverride = bgmDetailOpen
                  ? effectivePromptDraft
                  : { ...effectivePromptDraft, genre: '', mood: '', bpm: null, musical_key: '' };
                // song_description も genre も空なら override を送らず、サーバ側の
                // 既定テンプレート / 自動生成にフォールバックする (bgm_generator の
                // 空ガード条件と一致)。詳細トグルが閉じて genre が落ちた場合などでも
                // 生成を失敗させず「おまかせ」で生成する。
                const overrideToSend =
                  effectiveOverride &&
                  !(effectiveOverride.song_description || '').trim() &&
                  !(effectiveOverride.genre || '').trim()
                    ? null
                    : effectiveOverride;
                const result = isLyricsPromptMode
                  ? await generateScenarioBgm(scenario.id, {
                      provider: 'lyria-pro',
                      lyricsMode: true,
                      singerProfile: buildSingerProfileText(bgmSingerGender, bgmSingerText),
                      // 曲の設定 (genre/mood/bpm/key) と song_description のみ送る。
                      // is_instrumental / bgmTemplateKey / durationSeconds は送らない (§3.3)。
                      promptOverride: overrideToSend,
                      useStoryboardReference: bgmUseStoryboardRef,
                    })
                  // Lyria クリップは固定尺なので尺指定は送らない。
                  // テンプレート時は key だけ送る (本文はサーバー側で解決・インスト固定)。
                  : await generateScenarioBgm(scenario.id, {
                      promptOverride: templateMode ? null : overrideToSend,
                      bgmTemplateKey: templateMode ? effectiveTemplateKey : null,
                      durationSeconds: isLyriaClip ? undefined : effectiveBgmLengthSeconds,
                      provider: bgmProvider,
                      // テンプレート時は参照画像を使わない (絵コンテの色味で曲調が変わらないように)
                      useStoryboardReference: isLyriaFamily ? (templateMode ? false : bgmUseStoryboardRef) : undefined,
                      // 歌の有無トグル (全プロバイダ横断)。true=ボーカルあり → is_instrumental=false
                      isInstrumental: templateMode ? true : !bgmVocal,
                    });
                const materialId = result.material_id;
                if (lastResult) {
                  // 再生成したら「採用しない」は解除する (バックエンドも同時に解除する)
                  setLastResult((prev) => {
                    if (!prev) return prev;
                    const updatedScenarios = prev.scenarios.map((s) =>
                      s.id === scenario.id ? { ...s, bgm_material_id: materialId, bgm_excluded: false } : s,
                    );
                    return { ...prev, scenarios: updatedScenarios };
                  });
                }
                // 歌 → 映像 (計画 md §5.5.1): BE が歌の実測でコマ尺を書き戻したら、画面の
                // シーン (lastResult) と コマ尺のローカル state を同じ値にパッチする (再取得しない)。
                // generation_metadata.lyrics_sync はゲート解除・自動分割の判断材料。
                const lyricsSync = result.lyrics_sync;
                if (lyricsSync && materialId) applyLyricsSyncToState(lyricsSync, materialId);
                // 他シナリオを表示中なら、共有の再生 UI は更新しない。素材は戻ったときに読み込む。
                if (isBgmScenarioActive(scenario.id)) {
                  if (result.preview_url) {
                    setBgmAudioUrl(result.preview_url);
                  }
                  // 表示名は読み込み時の規則 (name > song_title > 「BGM (自動生成)」) に揃える。
                  // 生成直後は name が無いので song_title、無ければ自動生成ラベル。
                  setBgmFileName(result.bgm_prompt?.song_title || GENERATED_BGM_LABEL);
                  setBgmSongTitle(result.bgm_prompt?.song_title || null);
                  if (result.music_length_ms) setBgmDuration(Math.round(result.music_length_ms / 1000));
                  // 動画/ナレーションと同様、生成完了で再生ボタンにパルスを出す (再生で消える)。
                  setBgmUnplayed(true);
                  // 層 B (§3.6): 歌詞モードで生成した BGM は is_vocal_lead=true。
                  setBgmIsVocalLead(isLyricsPromptMode);
                  if (isLyricsPromptMode && materialId) {
                    // 「送った歌詞」/「Lyria が返した歌詞」の比較表示用に素材メタを取り直す
                    // (生成レスポンスには含まれないため)。
                    fetchMaterialById(materialId)
                      .then((m) => setGeneratedLyricsMeta(extractGeneratedLyricsMeta(m as { generation_metadata?: Record<string, unknown> | null })))
                      .catch(() => {});
                  } else {
                    setGeneratedLyricsMeta(null);
                  }
                }
                // 歌詞モードで前奏を自動で切り落としたときは、切った秒数を添える (切っていなければ従来どおり)。
                const trimmedSec = result.intro_trim?.status === 'trimmed' ? result.intro_trim.trimmed_sec : null;
                const toastNotes: string[] = [];
                if (trimmedSec != null && trimmedSec > 0) toastNotes.push(`前奏 ${trimmedSec.toFixed(1)} 秒を自動でカット`);
                // 歌に合わせてコマ尺を書き戻したときは合計秒を添える (動画カードの尺はこれに追随する)
                if (lyricsSync?.status === 'applied') toastNotes.push(`歌に合わせてシーンの尺を更新 (合計 ${lyricsSync.total_seconds} 秒)`);
                toast.success(toastNotes.length > 0 ? `BGMを生成しました (${toastNotes.join('・')})` : 'BGMを生成しました');
              } catch (err) {
                // ジョブの error_message (ElevenLabs の bad_prompt / Gemini の安全フィルタ理由
                // など) をそのまま出す。プロンプト起因の失敗は理由が見えないと直せない。
                toast.error(getErrorMessage(err, 'BGMの生成に失敗しました'));
              } finally {
                finishBgmGeneration(scenario.id);
              }
            }}
            disabled={
              bgmGenerating || !isValidScenarioId || bgmManualMode || templateOptionsPending
              || (isLyricsPromptMode && (lyricsPreviewLoading || !lyricsPreview || !lyricsPreview.ok))
            }
            title={
              bgmUploadMode ? 'アップロードタブでは生成できません'
                : bgmFreeTrackMode ? 'フリー楽曲タブでは生成できません'
                : !isValidScenarioId ? 'シナリオが保存されていません'
                : templateOptionsPending ? 'BGMテンプレートを読み込み中です'
                : (isLyricsPromptMode && lyricsPreview && !lyricsPreview.ok) ? lyricsPreview.reason
                : (isLyricsPromptMode && !lyricsPreview) ? '歌詞プレビューを取得中です'
                : undefined
            }
          >
            {bgmGenerating ? 'BGM生成中...' : (
              <>
                {/* BGM が付いている (外していない) ときは作り直しになるため「再生成」と出す */}
                <span>{scenario.bgm_material_id && !scenario.bgm_excluded ? 'BGMを再生成' : 'BGMを生成'}</span>
                {(() => {
                  // アップロード / フリー楽曲タブは生成しないためポイントは 0 表示。
                  if (bgmManualMode) {
                    return <span className={styles.creditBadge}>0</span>;
                  }
                  // Lyria 系は 1 曲固定課金。credits_per_unit をそのまま提示する。
                  if (isLyriaFamily) {
                    const lyriaRate = creditRatesData?.rates.find(
                      (r) => r.service_type === 'bgm'
                        && r.resource_type === (isLyriaPro ? 'lyria-pro-music' : 'lyria-clip-music'),
                    );
                    if (!lyriaRate) return null;
                    const credits = Math.max(1, Math.ceil(lyriaRate.credits_per_unit));
                    return <span className={styles.creditBadge}>{credits}</span>;
                  }
                  const bgmRate = creditRatesData?.rates.find(
                    (r) => r.service_type === 'bgm' && r.resource_type === 'elevenlabs-music',
                  );
                  if (!bgmRate) return null;
                  // credits_per_unit は「1分あたり」。尺 (秒) に応じて秒按分し ceil で整数化。
                  // 優先順位 (backend の BGM 生成ロジックと揃える):
                  //   0. UI の実効値 (ユーザー入力 > ストーリーボードのシーン合計既定値) が最優先
                  //   1. scenario.generation_metadata.duration ("15秒" 等)
                  //   2. 想定尺が無いときのみ scenes 合計にフォールバック
                  //      (UI 選択中 sceneVideoSettings[key].duration →
                  //       scenes[].video_duration_seconds → UI デフォルト 4 秒)
                  let seconds = 0;
                  if (typeof effectiveBgmLengthSeconds === 'number' && effectiveBgmLengthSeconds > 0) {
                    seconds = effectiveBgmLengthSeconds;
                  }
                  if (seconds <= 0) {
                    const durationStr = (scenario.generation_metadata as Record<string, unknown> | undefined)?.duration;
                    if (typeof durationStr === 'string') {
                      const m = /(\d+)/.exec(durationStr);
                      if (m) seconds = Number(m[1]);
                    }
                  }
                  if (seconds <= 0) {
                    const scenesArr = scenario.scenes || [];
                    seconds = scenesArr.reduce((acc, s) => {
                      const sceneKey = `${scenario.id}-scene-${s.order}`;
                      const uiDuration = getSceneVideoSettings(sceneKey)?.duration;
                      return acc + (uiDuration ?? s.video_duration_seconds ?? 4);
                    }, 0);
                  }
                  // 1・2 の既定値は backend と同じく30秒未満を切り上げる
                  if (seconds > 0 && !(typeof effectiveBgmLengthSeconds === 'number' && effectiveBgmLengthSeconds > 0)) {
                    seconds = toBgmDefaultLengthSeconds(seconds);
                  }
                  const credits = seconds > 0
                    ? Math.max(1, Math.ceil(bgmRate.credits_per_unit * seconds / 60))
                    : Math.floor(bgmRate.credits_per_unit);
                  return <span className={styles.creditBadge}>{credits}</span>;
                })()}
              </>
            )}
          </button>
        </div>
      </div>
      <div className={`${styles.expandContent} ${bgmExpanded ? styles.expanded : styles.collapsed}`}>
        {/* 設定群の内側ボディ。動画の .stepContent と共通の .expandBody を compose し、
            左右パディング・基本フォント・ブロック間隔を一元管理する。
            結果エリア (再生/履歴/DL) は全幅バーのためこの外に置く。 */}
        <div className={styles.bgmBody}>
        {/* 上: 入力方法タブ (ElevenLabs / Gemini / フリー楽曲 / アップロード)。タブに応じた
            詳細設定 (生成設定 or 楽曲一覧 or アップロード領域) を下に出す。 */}
        <div className={styles.bgmTopGridContainer}>
        <div className={styles.bgmTopGrid}>
        <div className={styles.bgmTopMainCol}>
        {/* 上: 入力方法タブ (ElevenLabs / Gemini / フリー楽曲 / アップロード)。他の生成エンジン切替と
            同じセグメンテッドデザイン。フル曲 (lyria-pro) への切替やプロンプト・曲の設定、
            楽曲一覧・アップロード領域は区切り線より下 (詳細設定) に置く。
            フリー楽曲 = 運営 / パートナー提供のカタログから選ぶ (ポイント消費なし・
            docs/features/free-bgm-catalog-plan.md)。 */}
        <div className={styles.bgmProviderRow} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
          <span className={styles.bgmInstructionLabel}>作曲AIモデル</span>
          <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center' }}>
            <SegmentedTabs
              tone="neutral"
              ariaLabel="BGM の入力方法"
              options={[
                {
                  value: 'elevenlabs' as const,
                  label: 'ElevenLabs',
                  iconMaskUrl: '/logos/elevenlabs-symbol.svg',
                  tooltip: isLyricsPromptMode ? '歌詞モードは Gemini (フル曲) のみ対応' : '秒数を指定して生成',
                  disabled: isLyricsPromptMode,
                },
                { value: 'gemini' as const, label: 'Gemini', iconMaskUrl: '/logos/google_gemini_icon.svg', tooltip: '30秒クリップを生成し、タイムラインに合わせてループ' },
                { value: 'free' as const, label: 'フリー楽曲', iconMaskUrl: '/music_on_off_1.svg', tooltip: '用意された楽曲から選ぶ (ポイント消費なし)' },
                { value: 'upload' as const, label: 'アップロード', iconMaskUrl: '/audio_file_mp_3.svg', tooltip: '手持ちの音源ファイルを使う' },
              ]}
              value={
                bgmFreeTrackMode ? ('free' as const)
                  : bgmUploadMode ? ('upload' as const)
                  : (bgmProvider === 'elevenlabs' ? ('elevenlabs' as const) : ('gemini' as const))
              }
              onChange={(v) => {
                // アップロードとフリー楽曲は排他 (どちらも生成しないタブ)
                setBgmUploadMode(v === 'upload');
                setBgmFreeTrackMode(v === 'free');
                if (v === 'upload' || v === 'free') return;
                if (v === 'elevenlabs') setBgmProvider('elevenlabs');
                else if (!isLyriaPro) setBgmProvider('lyria-pro');
              }}
              disabled={bgmGenerating}
            />
          </div>
        </div>
        </div>
        </div>
        </div>
        {/* 下: アップロードタブのとき音源ファイルのアップロード領域を表示。 */}
        {bgmUploadMode && !bgmUploading && (
          <BgmUploadDropZone
            onDropExistingMaterial={(asset) => { void handleDropExistingBgmMaterial(asset); }}
            onFile={(file) => {
              // 上の hidden input に載せて change を発火し、通常のアップロード経路に合流させる
              if (!bgmFileInputRef.current) return;
              const dt = new DataTransfer();
              dt.items.add(file);
              bgmFileInputRef.current.files = dt.files;
              bgmFileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
            }}
            onChooseFile={() => bgmFileInputRef.current?.click()}
          />
        )}
        {/* 下: フリー楽曲タブのとき、選択中の曲と「楽曲を選ぶ」ボタン。一覧 (試聴 + 選択) は
            モーダルで出す (楽曲は多くなるため)。 */}
        {bgmFreeTrackMode && (
          <BgmFreeTrackPicker
            selectedCatalogId={scenario.bgm_excluded ? null : bgmCatalogItemId}
            selectedName={bgmFileName || bgmSongTitle}
            selectedDurationSeconds={bgmDuration}
            busyCatalogId={freeTrackBusyId}
            disabled={bgmGenerating || !isValidScenarioId}
            onPick={handlePickFreeTrack}
          />
        )}
        {/* 下: 生成設定 (アップロード / フリー楽曲タブのときは非表示)。
            並び順: BGM生成プロンプト → 曲の設定 → (Gemini) ストーリーボード参照 →
            (Gemini) クリップ/フル曲 モード。
            プロンプトと曲の設定を ElevenLabs/Gemini で共通の先頭に置くことで、
            エンジン切替時に画面が動かないようにしている。 */}
        {!bgmManualMode && (
        <>
        {/* 上: プロンプトの入力方法 (標準 / テンプレート / 歌詞)。テンプレートは属性ツリーに
            BGM テンプレートがあるときだけ選択肢に足す。歌詞 = シーンの narration を歌詞として
            Lyria Pro に歌わせる (docs/features/scenario-song-lyrics-bgm-plan.md §5.5)。
            歌モノシナリオはソフトロックでこのセレクタごと disabled になる (§6.1)。 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }} onClick={(e) => e.stopPropagation()}>
          {/* ナレーターの音声AIモデル切替と同じ小さなセグメント */}
          <ProviderSegmentedControl
            options={[
              { value: 'standard' as const, label: '標準', tooltip: 'シナリオから自動生成したプロンプトを編集して作曲' },
              ...(bgmTemplateOptions.length > 0
                ? [{ value: 'template' as const, label: 'テンプレート', tooltip: '用意された曲調テンプレートから選んで作曲' }]
                : []),
              // 歌詞は歌モノシナリオ (bgm_mode==='lyrics') だけに出す (2026-09-17 ユーザー判断)。
              // 通常シナリオのナレーションは歌詞として整っておらず、歌モノにするならシナリオ企画時に
              // 歌モノトーンを選ぶ方が本文も歌詞向けになるため。
              ...(isLyricsModeScenario(scenario)
                ? [{ value: 'lyrics' as const, label: '歌詞', tooltip: 'シーンのナレーションを歌詞にして Lyria (フル曲) に歌わせる' }]
                : []),
            ]}
            value={bgmPromptMode}
            // 歌詞モードは Lyria Pro 固定・ボーカルあり固定 (§3.3)。localStorage には残さない。
            onChange={selectBgmPromptMode}
            disabled={bgmGenerating || bgmLyricsLocked}
          />
          {/* 「歌詞モードを解除して通常の BGM を作る」リンクは 2026-09-15 に撤去 (歌モノシナリオでは
              歌詞モード固定でよい、というユーザー判断)。useBgmEditor.releaseBgmLyricsLock は残置。 */}
          {templateMode && (
            // トリガーは .button が width:100% のため、inline-flex で包んでテキスト幅に収める
            <div style={{ display: 'inline-flex' }}>
              <SingleSelectDropdown
                label="BGMテンプレート"
                labelOnly
                alwaysShowSelected
                fitContent
                value={effectiveTemplateKey ?? ''}
                onChange={(v) => setBgmTemplateKey(v)}
                options={bgmTemplateOptions}
                disabled={bgmGenerating}
              />
            </div>
          )}
        </div>
        {/* 標準モードのみ: プロンプト編集 / ボーカル / 曲の詳細。テンプレート時は本文を見せず
            インスト固定のため丸ごと出さない。 */}
        {!templateMode && (
        <>
        {/* 下: BGM生成プロンプト編集 (全プロバイダ)。鉛筆アイコンで開閉トグル。
            歌詞モードでは出さない (プロンプトは歌詞 + 固定文が主体で、song_description は
            サーバー既定に任せる。2026-09-15)。 */}
        {!isLyricsPromptMode && (
        <div>
          <div className={styles.bgmPromptEditIconRow}>
            <span className={styles.bgmInstructionLabel}>BGM生成プロンプト</span>
            <Tooltip content="BGM生成プロンプトを編集">
              <button
                type="button"
                className={styles.bgmPromptEditIconBtn}
                onClick={async (e) => {
                  e.stopPropagation();
                  const next = !bgmPromptEditing;
                  setBgmPromptEditing(next);
                  if (next) setBgmExpanded(true);
                  if (next && !bgmPromptDraft && !bgmPromptFetching) {
                    setBgmPromptFetching(true);
                    try {
                      const prompt = await fetchScenarioBgmPrompt(scenario.id, bgmProvider, isLyriaFamily ? bgmUseStoryboardRef : undefined);
                      setBgmPromptDraft(prompt);
                      persistPromptDraft(prompt);
                    } catch {
                      toast.error('BGMプロンプトの取得に失敗しました');
                      setBgmPromptEditing(false);
                    } finally {
                      setBgmPromptFetching(false);
                    }
                  }
                }}
                aria-label="BGMプロンプトを編集"
              >
                <img src="/pencil_1.svg" alt="" />
              </button>
            </Tooltip>
            {bgmPromptEditing && !bgmPromptFetching && (
              <button
                type="button"
                className={styles.bgmPromptRefetchBtn}
                style={{ marginLeft: 'auto' }}
                onClick={async (e) => {
                  e.stopPropagation();
                  setBgmPromptFetching(true);
                  try {
                    const prompt = await fetchScenarioBgmPrompt(scenario.id, bgmProvider, isLyriaFamily ? bgmUseStoryboardRef : undefined);
                    setBgmPromptDraft(prompt);
                    persistPromptDraft(prompt);
                    toast.success('BGM生成プロンプトを再生成しました');
                  } catch {
                    toast.error('BGMプロンプトの取得に失敗しました');
                  } finally {
                    setBgmPromptFetching(false);
                  }
                }}
                disabled={bgmPromptFetching}
              >
                再生成
              </button>
            )}
          </div>
          <div className={styles.bgmConfigRow}>
            <div
              className={`${styles.bgmPromptEditor} ${bgmPromptEditing ? styles.bgmPromptEditorOpen : ''}`}
              aria-hidden={!bgmPromptEditing}
            >
              {bgmPromptFetching ? (
                <span className={styles.bgmPromptEditorStatus}>プロンプトを生成中...</span>
              ) : (
                <textarea
                  className={styles.bgmInstructionTextarea}
                  value={effectivePromptDraft.song_description || ''}
                  onChange={(e) => updatePromptDraft({ song_description: e.target.value })}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Upbeat lo-fi hip hop with mellow piano, 90 BPM"
                  rows={3}
                  disabled={bgmGenerating || !bgmPromptEditing}
                  tabIndex={bgmPromptEditing ? 0 : -1}
                />
              )}
            </div>
          </div>
        </div>
        )}
        {/* 下: ボーカル (BGM 直下の独立チェックボックス)。全プロバイダ横断・localStorage 保持。
            ElevenLabs は force_instrumental パラメータ、Gemini はプロンプトの一文に反映。
            歌詞モードは常にボーカルあり (§3.3/§5.5) のためチェックボックス自体を出さない。 */}
        {!isLyricsPromptMode && (
        <div onClick={(e) => e.stopPropagation()}>
          <label className="form-checkbox-label form-checkbox-label--compact">
            <input
              type="checkbox"
              checked={bgmVocal}
              onChange={(e) => setBgmVocal(e.target.checked)}
              disabled={bgmGenerating}
            />
            <span>ボーカルを含める</span>
          </label>
        </div>
        )}
        {/* 歌 → 映像の再同期 (計画 md §5.2.5): BGM 生成時に同じ解析・書き戻しが自動で走るので、
            これは保険 (生成時の自動同期が見送られたとき / シーン尺を手で触ったあとに歌へ戻す)。
            目立たないテキストボタン (「歌詞の比較」リンクと同じ体裁)。「曲の詳細を設定」トグルの直上に置く (ユーザー指定)。BGM の再生成・
            クレジット消費はない。 */}
        {isLyricsPromptMode && videoGenerationMode === 'storyboard' && scenario.bgm_material_id && !scenario.bgm_excluded && bgm.bgmIsVocalLead && (
          <button
            type="button"
            disabled={lyricsSyncing || bgmGenerating}
            title="採用中の歌を聞き取り直し、歌詞の歌い出しに合わせて各シーンのコマ尺を書き換えます"
            onClick={async (e) => {
              e.stopPropagation();
              if (lyricsSyncing) return;
              setLyricsSyncing(true);
              try {
                const res = await syncScenarioLyricsTiming(scenario.id);
                const sync = res.lyrics_sync;
                if (sync.status === 'applied') {
                  applyLyricsSyncToState(sync, res.material_id);
                  toast.success(`歌に合わせてシーンの尺を更新しました (合計 ${sync.total_seconds} 秒・照合 ${sync.matched_count}/${sync.scene_count} シーン)`);
                } else {
                  toast.error(describeLyricsSyncSkip(sync));
                }
              } catch (err) {
                toast.error(getErrorMessage(err, 'シーンの尺の同期に失敗しました'));
              } finally {
                setLyricsSyncing(false);
              }
            }}
            style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.5)', background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: lyricsSyncing ? 'default' : 'pointer', textDecoration: 'underline', opacity: lyricsSyncing ? 0.6 : 1 }}
          >
            {lyricsSyncing ? '歌を解析中…' : '歌に合わせてシーンの尺を再同期'}
          </button>
        )}
        {/* BGM の小節頭に合わせてシーンの尺を丸める (計画 md §15.5・Phase 2d)。歌詞再同期と同じ目立たないテキストボタン。
            押すとプレビュー (変更のあるシーンの一覧 + 注記) を出し、「適用」で予定尺を書き戻す。ポイント消費なし。 */}
        {/* 全編モーション (§10.5) は計画 (motion_plan) が尺を決めるので、境界丸めの代わりに「計画を更新」を出す */}
        {isMotionEngineScenario(scenario) && isValidScenarioId && (
          <MotionReplanLink scenario={scenario} disabled={bgmGenerating} />
        )}
        {canRoundMotionBoundaries && !isMotionEngineScenario(scenario) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              disabled={boundaryRoundingBusy || bgmGenerating}
              title="未生成のシーンの予定尺を少しずつ動かし、シーンの切れ目を BGM の小節頭に揃えます (動画・静止画・ブランドカットの尺は変えません)"
              onClick={handlePreviewBoundaryRounding}
              style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.5)', background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: boundaryRoundingBusy ? 'default' : 'pointer', textDecoration: 'underline', opacity: boundaryRoundingBusy ? 0.6 : 1 }}
            >
              {boundaryRoundingBusy && !boundaryRounding ? 'BGM を解析中…' : 'BGM の小節に合わせてシーンの尺を丸める'}
            </button>
            {boundaryRounding && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px 10px', border: '1px solid rgba(245, 241, 230, 0.15)', borderRadius: '6px', fontSize: '12px', color: 'rgba(245, 241, 230, 0.75)' }}>
                {changedBoundaryRoundingRows(boundaryRounding).map((row) => (
                  <span key={row.sceneId}>{row.text}</span>
                ))}
                <span style={{ color: 'rgba(245, 241, 230, 0.5)' }}>{MOTION_BOUNDARY_ROUNDING_NOTE}</span>
                {boundaryRounding.note && (
                  <span style={{ color: 'rgba(245, 241, 230, 0.5)' }}>{boundaryRounding.note}</span>
                )}
                <div style={{ display: 'flex', gap: '12px', marginTop: '2px' }}>
                  <button
                    type="button"
                    disabled={boundaryRoundingBusy}
                    onClick={handleApplyBoundaryRounding}
                    style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.9)', background: 'none', border: 'none', padding: 0, cursor: boundaryRoundingBusy ? 'default' : 'pointer', textDecoration: 'underline', opacity: boundaryRoundingBusy ? 0.6 : 1 }}
                  >
                    {boundaryRoundingBusy ? '適用中…' : '適用'}
                  </button>
                  <button
                    type="button"
                    disabled={boundaryRoundingBusy}
                    onClick={() => setBoundaryRounding(null)}
                    style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.5)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    キャンセル
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {/* 下: 曲の詳細設定 (折りたたみ・既定 On)。ジャンル/ムード/BPM/キー。 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }} onClick={(e) => e.stopPropagation()}>
          <label className="form-checkbox-label form-checkbox-label--compact">
            <input
              type="checkbox"
              checked={bgmDetailOpen}
              onChange={(e) => handleBgmDetailToggle(e.target.checked)}
              disabled={bgmGenerating}
            />
            <span>曲の詳細を設定する</span>
          </label>
          {bgmDetailOpen && (
              <div className={styles.optionFormBody}>
              <BgmSongDetailFields
                value={effectivePromptDraft}
                onChange={updatePromptDraft}
                disabled={bgmGenerating}
                genreSelect={isLyricsPromptMode ? (
                  <SingleSelectDropdown
                    label="ジャンル"
                    labelOnly
                    options={LYRICS_GENRE_OPTIONS}
                    triggerClassName={`${styles.bgmDetailControl} ${styles.bgmGenreSelect}`}
                    dropdownClassName={styles.bgmGenreMenu}
                    value={isCustomGenre ? 'custom' : effectivePromptDraft.genre!}
                    onChange={(genre) => {
                      setCustomGenreSelected(genre === 'custom');
                      if (genre !== 'custom') updatePromptDraft({ genre });
                    }}
                    disabled={bgmGenerating}
                    fitContent
                    flipUp
                  />
                ) : null}
                showGenreInput={!isLyricsPromptMode || isCustomGenre}
                genreInputLabel={isLyricsPromptMode ? 'カスタムジャンル' : 'ジャンル'}
              />
              </div>
          )}
        </div>
        {/* 下: 歌詞モードの追加設定 (歌手プロファイル・歌詞プレビュー・注記)。§5.5 */}
        {isLyricsPromptMode && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }} onClick={(e) => e.stopPropagation()}>
            {/* 通常シナリオ (bgm_mode!=='lyrics') で手動選択したときだけの注記 (§5.5)。
                ロック解除後の歌モノシナリオ (bgm_mode==='lyrics') には出さない
                (歌モノ本文はもともと歌詞前提のため、この注記の対象外)。 */}
            {!bgmLyricsLocked && !isLyricsModeScenario(scenario) && (
              <div style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.45)', lineHeight: 1.5 }}>
                ナレーション文をそのまま歌詞として歌います。この BGM を採用すると統合動画では
                ナレーション音声が無音になります。
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span className={styles.bgmInstructionLabel}>歌手 (任意)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <SingleSelectDropdown
                  label="歌手"
                  labelOnly
                  alwaysShowSelected
                  fitContent
                  unstyledTrigger
                  triggerClassName={styles.bgmSingerTrigger}
                  value={bgmSingerGender}
                  onChange={(v) => setBgmSingerGender(v as SingerGender)}
                  options={[
                    { value: 'unspecified', label: '指定なし' },
                    { value: 'female', label: '女性' },
                    { value: 'male', label: '男性' },
                  ]}
                  disabled={bgmGenerating}
                />
                <input
                  type="text"
                  value={bgmSingerText}
                  onChange={(e) => setBgmSingerText(e.target.value)}
                  placeholder="声質の自由記述 (例: 澄んだ明るい声)"
                  disabled={bgmGenerating}
                  style={{ flex: '1 1 200px', minWidth: 0, boxSizing: 'border-box', fontSize: '13px', padding: '4px 6px', borderRadius: '4px', border: '1px solid rgba(245, 241, 230, 0.15)', background: 'rgba(0, 0, 0, 0.2)', color: 'rgba(245, 241, 230, 0.85)' }}
                />
              </div>
            </div>
            {/* 歌詞プレビューの一覧表示と編集場所の注記は 2026-09-15 に撤去 (歌詞はシーン欄の
                narration そのもので二重表示になるため)。lyricsPreview の取得は前提検証 (生成ボタンの
                活性・想定尺表示・エラー理由のツールチップ) に引き続き使う。 */}
            {generatedLyricsMeta && (generatedLyricsMeta.lyrics_input || generatedLyricsMeta.lyrics_sections) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <button
                  type="button"
                  onClick={() => setLyricsComparisonExpanded((prev) => !prev)}
                  style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.5)', background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {lyricsComparisonExpanded ? '歌詞の比較を閉じる' : '送った歌詞と歌われた歌詞を比較する'}
                </button>
                {lyricsComparisonExpanded && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.6)', whiteSpace: 'pre-wrap' }}>
                      <div style={{ color: 'rgba(245, 241, 230, 0.4)', marginBottom: '4px' }}>送った歌詞</div>
                      {generatedLyricsMeta.lyrics_input || '-'}
                    </div>
                    <div style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.6)' }}>
                      <div style={{ color: 'rgba(245, 241, 230, 0.4)', marginBottom: '4px' }}>Lyria が返した歌詞</div>
                      {generatedLyricsMeta.lyrics_sections && generatedLyricsMeta.lyrics_sections.length > 0
                        ? generatedLyricsMeta.lyrics_sections.map((sec, i) => (
                            <div key={i} style={{ marginBottom: '6px' }}>
                              {sec.tag && <div style={{ color: 'rgba(245, 241, 230, 0.4)' }}>[{sec.tag}]</div>}
                              {sec.lines.map((line, li) => <div key={li}>{line}</div>)}
                            </div>
                          ))
                        : '-'}
                    </div>
                  </div>
                )}
                <span style={{ fontSize: '11px', color: 'rgba(245, 241, 230, 0.3)' }}>
                  この比較は構成・行数の確認用で、歌唱の聞き取りやすさは保証しません。
                </span>
              </div>
            )}
          </div>
        )}
        </>
        )}
        {/* 下: Lyria 系の参照画像チェックボックス (クリップ / フル曲 共通)。曲の設定の下・
            モード切替の上に置き、エンジン切替で曲の設定の位置が動かないようにする。
            添付される画像は動画生成モードに応じて変わるため、文言もモード別にする。 */}
        {isLyriaFamily && !templateMode && (
          <div onClick={(e) => e.stopPropagation()}>
            <label className="form-checkbox-label form-checkbox-label--compact">
              <input
                type="checkbox"
                checked={bgmUseStoryboardRef}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setBgmUseStoryboardRef(checked);
                  // 下書きの song_description が「未編集の既定テンプレート」のままなら、
                  // 新しいチェック値のテンプレートに引き直す (ユーザーが編集済みなら触らない)。
                  setBgmPromptDraft((prev) => {
                    if (!prev) return prev;
                    const oldTemplate = geminiDefaultSongDescription(scenario.summary, !checked, videoGenerationMode);
                    if ((prev.song_description || '') !== oldTemplate) return prev;
                    const next = { ...prev, song_description: geminiDefaultSongDescription(scenario.summary, checked, videoGenerationMode) };
                    persistPromptDraft(next);
                    return next;
                  });
                }}
                disabled={bgmGenerating}
              />
              <span>{bgmReferenceImageLabel}を作曲に利用する</span>
            </label>
          </div>
        )}
        {/* 下: Gemini はフル曲 (lyria-pro) 固定。クリップ (lyria) は尺を指定できず常に約 30 秒になるため 2026-09-29 に
            トグルごと外した。曲の展開 (sections タイムライン) は 2026-09-10 に廃止。尺はプロンプトの尺指示だけで制御する。 */}
        {isLyriaPro && !isLyricsPromptMode && (
          // 歌詞モードは尺の注記を出さない (2026-09-15)。
          <span style={{ fontSize: '13px', color: 'rgba(245, 241, 230, 0.35)' }} onClick={(e) => e.stopPropagation()}>
            ※ 生成される曲の長さは指定尺と完全一致しない場合があります
          </span>
        )}
        </>
        )}
        </div>
        {/* 結果エリア (再生 / 履歴 / DL) は全幅バーのため .bgmBody の外に置く。 */}
        {bgmUploading ? (
          <div className={styles.stepResultArea} style={{ justifyContent: 'center' }}>
            <span style={{ fontSize: '13px', color: 'rgba(245, 241, 230, 0.5)' }}>アップロード中...</span>
          </div>
        ) : (
          <>
            {bgmAudioUrl && (
              <audio
                ref={(el) => { bgmAudioRef.current = el; }}
                src={bgmAudioUrl}
                preload="metadata"
                style={{ display: 'none' }}
                onPlay={() => { setBgmPlaying(true); setBgmUnplayed(false); }}
                onPause={() => setBgmPlaying(false)}
                onEnded={() => setBgmPlaying(false)}
              />
            )}
            <div className={styles.stepResultArea} style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr' }}>
              <div style={{ justifySelf: 'start', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  type="button"
                  className={bgmUnplayed && !bgmPlaying ? styles.bgmPlayUnplayed : undefined}
                  disabled={!bgmAudioUrl}
                  onClick={() => {
                    if (!bgmAudioRef.current || !bgmAudioUrl) return;
                    if (bgmPlaying) {
                      bgmAudioRef.current.pause();
                    } else {
                      void playExclusive(bgmAudioRef.current).catch(() => setBgmPlaying(false));
                    }
                  }}
                  title={!bgmAudioUrl ? '再生できるBGMがありません' : scenario.bgm_excluded ? '採用なし（BGMを選ぶと採用されます）' : bgmPlaying ? '停止' : 'BGMを再生'}
                  style={{ border: 'none', background: 'transparent', cursor: bgmAudioUrl ? 'pointer' : 'default', padding: '0', display: 'inline-flex', alignItems: 'center', opacity: bgmAudioUrl ? 1 : 0.35 }}
                >
                  <img
                    src={bgmPlaying ? '/button_stop_orange.svg' : '/button_play_orange.svg'}
                    alt={bgmPlaying ? '停止' : '再生'}
                    style={{ width: '24px', height: '24px', ...(scenario.bgm_excluded ? { filter: 'grayscale(100%) opacity(0.4)' } : {}) }}
                  />
                </button>
                {isValidScenarioId && (
                  <BgmHistorySelector
                    scenarioId={scenario.id}
                    currentMaterialId={scenario.bgm_material_id}
                    selectedSongTitle={bgmFileName || bgmSongTitle || undefined}
                    durationSeconds={bgmDuration}
                    excluded={!!scenario.bgm_excluded}
                    onToggleExcluded={async (excluded) => {
                      // 「採用しない」トグル。素材紐付けは保持したまま合成/エクスポートから外す。
                      try {
                        await updateScenarioMutation.mutateAsync({
                          scenarioId: scenario.id,
                          payload: { bgm_excluded: excluded },
                        });
                        if (lastResult) {
                          setLastResult((prev) => {
                            if (!prev) return prev;
                            const updatedScenarios = prev.scenarios.map((s) =>
                              s.id === scenario.id ? { ...s, bgm_excluded: excluded } : s,
                            );
                            return { ...prev, scenarios: updatedScenarios };
                          });
                        }
                        // 層 B (§3.6): 採用しない=false 扱い。再度採用したら現在の素材の値に戻す。
                        if (excluded) {
                          setBgmIsVocalLead(false);
                        } else if (scenario.bgm_material_id) {
                          fetchMaterialById(scenario.bgm_material_id)
                            .then((m) => setBgmIsVocalLead(resolveMaterialIsVocalLead(m as { generation_metadata?: Record<string, unknown> | null })))
                            .catch(() => {});
                        }
                        toast.success(excluded ? 'BGMをプレビュー・エクスポートから除外しました' : 'BGMを採用に戻しました');
                      } catch {
                        toast.error('BGMの採用状態の変更に失敗しました');
                      }
                    }}
                    onSelect={async (materialId) => {
                      try {
                        // テイクを選び直したら「採用しない」は解除する
                        await updateScenarioMutation.mutateAsync({ scenarioId: scenario.id, payload: { bgm_material_id: materialId, bgm_excluded: false } });
                        if (lastResult) {
                          setLastResult((prev) => {
                            if (!prev) return prev;
                            const updatedScenarios = prev.scenarios.map((s) =>
                              s.id === scenario.id ? { ...s, bgm_material_id: materialId, bgm_excluded: false } : s,
                            );
                            return { ...prev, scenarios: updatedScenarios };
                          });
                        }
                        const url = await fetchMaterialSignedUrl(materialId, true);
                        if (url) setBgmAudioUrl(url);
                        const mat = await fetchMaterialById(materialId);
                        if (mat) {
                          setBgmFileName(resolveBgmDisplayName(mat as BgmDisplayNameSource) || 'BGM');
                          const meta = (mat as { generation_metadata?: { song_title?: string } }).generation_metadata;
                          setBgmSongTitle(meta?.song_title || null);
                          setBgmDuration((mat as { duration_seconds?: number }).duration_seconds || null);
                          // 層 B (§3.6) + 歌詞比較表示の追随。
                          setBgmIsVocalLead(resolveMaterialIsVocalLead(mat as { generation_metadata?: Record<string, unknown> | null }));
                          setGeneratedLyricsMeta(extractGeneratedLyricsMeta(mat as { generation_metadata?: Record<string, unknown> | null }));
                        }
                        toast.success('BGMを切り替えました');
                      } catch {
                        toast.error('BGMの切り替えに失敗しました');
                      }
                    }}
                    onDelete={async () => {
                      try {
                        if (bgmPlaying && bgmAudioRef.current) bgmAudioRef.current.pause();
                        await updateScenarioMutation.mutateAsync({
                          scenarioId: scenario.id,
                          payload: { bgm_material_id: null },
                        });
                        if (lastResult) {
                          setLastResult((prev) => {
                            if (!prev) return prev;
                            const updatedScenarios = prev.scenarios.map((s) =>
                              s.id === scenario.id ? { ...s, bgm_material_id: undefined } : s,
                            );
                            return { ...prev, scenarios: updatedScenarios };
                          });
                        }
                        setBgmAudioUrl(null);
                        setBgmFileName(null);
                        setBgmSongTitle(null);
                        setBgmDuration(null);
                        setBgmIsVocalLead(false);
                        setGeneratedLyricsMeta(null);
                        // プロンプト編集の下書きも破棄 (削除して再生成したいときに Gemini から新規取得させる)
                        setBgmPromptDraft(null);
                        setBgmPromptEditing(false);
                        localStorage.removeItem(`bgm_prompt_override_${scenario.id}`);
                      } catch {
                        toast.error('BGMの削除に失敗しました');
                      }
                    }}
                  />
                )}
              </div>
              <div />
              <div style={{ justifySelf: 'end' }}>
                <button
                  type="button"
                  className={styles.downloadButton}
                  onClick={() => {
                    if (scenario.bgm_material_id) {
                      const namePrefix = scenario.title.replace(/[\\/:*?"<>|]/g, '_');
                      handleDownloadMaterial(scenario.bgm_material_id, `${namePrefix}_bgm`);
                    }
                  }}
                  title="BGMをダウンロード"
                >
                  <img src="/download_bottom.svg" alt="ダウンロード" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
